import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";
import EpisodeList from "@/pages/detail/components/EpisodeList";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(() => {
    if (!video) return [];
    const sameType = videos.filter((item) => item.id !== video.id && item.type === video.type);
    const sharedTag = videos.filter(
      (item) =>
        item.id !== video.id &&
        item.type !== video.type &&
        item.tags.some((tag) => video.tags.includes(tag)),
    );
    return [...sameType, ...sharedTag].slice(0, 5);
  }, [video]);

  usePageMeta(
    video ? `${video.title} - 影视详情 - 芒果视频` : "影视详情 - 芒果视频",
    video
      ? `${video.title}：${video.summary}由${video.director}执导，${video.cast.join("、")}主演，${video.year}年${video.region}${video.type}，评分${video.rating}。`
      : "在芒果视频查看更多影视详情、剧集介绍与相关推荐。",
    video
      ? `${video.title},${video.tags.join(",")},${video.type}在线观看,${video.region}${video.type},芒果视频`
      : "影视详情,芒果视频"
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，快去分享给朋友吧");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center px-4 py-24 text-center md:px-6 lg:px-10">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">内容不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的影视内容可能已被移除，看看其他精彩内容吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
          <Link
            to="/category"
            className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-400"
          >
            浏览分类
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(video.id);

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6 md:py-8 lg:px-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category?type=${encodeURIComponent(video.type)}`} className="transition-colors hover:text-primary-400">
          {video.type}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-700">{video.title}</span>
      </nav>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-[220px] shrink-0 sm:w-[260px] lg:mx-0 lg:w-full">
          <div className="relative w-full overflow-hidden rounded-lg bg-background-200 aspect-[2/3]">
            <img
              src={video.cover}
              alt={`${video.title}封面`}
              className="h-full w-full object-cover object-top"
            />
            <span className="absolute right-2 top-2 flex items-center gap-0.5 rounded-md bg-background-950/70 px-2 py-1 font-label text-sm font-bold text-primary-400">
              <i className="ri-star-fill text-xs"></i>
              {video.rating.toFixed(1)}
            </span>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500/15 px-2 py-0.5 text-xs font-semibold text-primary-400">
              {video.type}
            </span>
            <span className="rounded-md bg-secondary-200/40 px-2 py-0.5 text-xs text-foreground-700">
              {video.status}
            </span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {video.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-foreground-600">
            <span>{video.year}</span>
            <span>{video.region}</span>
            <Link
              to={`/category?type=${encodeURIComponent(video.type)}`}
              className="transition-colors hover:text-primary-400"
            >
              {video.type}
            </Link>
            <span>共 {video.episodeCount > 1 ? video.episodeCount : 1} {video.type === "综艺" ? "期" : "集"}</span>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category?genre=${encodeURIComponent(tag)}`}
                className="rounded-full border border-background-200 px-2.5 py-0.5 text-xs text-foreground-600 transition-colors hover:border-primary-500/60 hover:text-primary-400"
              >
                {tag}
              </Link>
            ))}
          </div>

          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex gap-2">
              <dt className="w-14 shrink-0 text-foreground-500">导演</dt>
              <dd className="text-foreground-800">{video.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
              <dd className="text-foreground-800">{video.cast.join(" / ")}</dd>
            </div>
          </dl>

          <div className="mt-4">
            <h2 className="font-heading text-sm font-semibold text-foreground-900">内容简介</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{video.summary}</p>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(video.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                favorited
                  ? "border border-primary-500 text-primary-400"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"}></i>
              {favorited ? "已收藏" : "加入收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-400"
            >
              <i className="ri-share-forward-line text-lg"></i>
              分享链接
            </button>
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-information-line"></i>
            收藏保存在浏览器本地（演示），播放与下载功能暂未接入，仅作内容展示。
          </p>
        </div>
      </div>

      <div className="mt-8">
        <EpisodeList video={video} />
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
            <Link
              to="/category"
              className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-400 transition-colors hover:text-primary-300"
            >
              浏览更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {related.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-full bg-background-950/90 px-5 py-2.5 text-sm text-foreground-950 backdrop-blur md:bottom-8">
          {toast}
        </div>
      )}
    </div>
  );
}